// @ts-ignore
// @ts-ignore
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { getLogoUrlSync, getLogoUrl } from '../../../utils/logoUtils';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import axios from 'axios';

// API URL
// If REACT_APP_API_URL is set to '/api', we don't want to add another '/api' in the requests
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

// Get visitor ID from session storage
const getVisitorId = (): string | null => {
  return sessionStorage.getItem('visitorId');
};

// Track visitor activity (pre-login)
const trackVisitor = async (page: string, activity: string, email?: string) => {
  try {
    const visitorId = getVisitorId();
    if (!visitorId) return;
    const utmData: Record<string, string> = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
      const val = sessionStorage.getItem(key);
      if (val) utmData[key] = val;
    });
    await fetch(`${API_BASE}/api/visitor/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, page, activity, email, ...utmData })
    });
  } catch (error) {
    console.debug('Visitor tracking failed:', error);
  }
};

// Convert visitor to logged-in user
const convertVisitor = async (email: string) => {
  try {
    const visitorId = getVisitorId();
    if (!visitorId) return;
    
    await fetch(`${API_BASE}/api/visitor/convert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visitorId, email })
    });
    
    // Clear visitor ID after successful conversion
    sessionStorage.removeItem('visitorId');
  } catch (error) {
    console.debug('Visitor conversion failed:', error);
  }
};

// Styled components for a more modern UI
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: -webkit-fill-available; /* iOS Safari fix */
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  
  @media (max-width: 480px) {
    padding: 12px;
    min-height: 100dvh;
    align-items: flex-start;
    padding-top: 40px;
  }
  
  @media (max-width: 375px) {
    padding: 10px;
    padding-top: 30px;
  }
`;

const Card = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  padding: 24px 28px;
  width: 100%;
  max-width: 420px;
  
  @media (max-width: 480px) {
    padding: 18px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 375px) {
    padding: 14px;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 12px;
  position: relative;
`;

const Logo = styled.img`
  max-width: 339px;
  max-height: 75px;
  height: auto;
  object-fit: contain;
  
  @media (max-width: 480px) {
    max-width: 260px;
    max-height: 55px;
  }
  
  @media (max-width: 375px) {
    max-width: 220px;
    max-height: 48px;
  }
`;

const HomeButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: #4a6cf7;
  display: flex;
  align-items: center;
  font-size: 14px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  text-align: center;
  margin-bottom: 0;
  
  @media (max-width: 480px) {
    font-size: 18px;
  }
  
  @media (max-width: 375px) {
    font-size: 16px;
  }
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #718096;
  text-align: center;
  margin-bottom: 0;
  margin-top: 2px;
  line-height: 1.4;
  word-break: break-word;
  
  strong {
    color: #495057;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const OtpInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
  
  @media (max-width: 480px) {
    gap: 6px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 375px) {
    gap: 4px;
  }
`;

const OtpInput = styled.input`
  width: 45px;
  height: 50px;
  font-size: 20px;
  font-weight: 500;
  text-align: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin: 0;
  transition: all 0.2s ease;
  -webkit-appearance: none; /* Remove iOS default styling */
  
  &:focus {
    border-color: #4c6ef5;
    box-shadow: 0 0 0 2px rgba(76, 110, 245, 0.1);
    outline: none;
  }
  
  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    width: 42px;
    height: 48px;
    font-size: 18px;
    border-radius: 6px;
  }
  
  @media (max-width: 375px) {
    width: 38px;
    height: 44px;
    font-size: 16px;
  }
  
  @media (max-width: 320px) {
    width: 34px;
    height: 40px;
    font-size: 15px;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px 16px;
  font-size: 15px;
  font-weight: 500;
  background-color: #4c6ef5;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px; /* Touch-friendly */
  -webkit-tap-highlight-color: transparent;
  
  &:hover {
    background-color: #3b5bdb;
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(76, 110, 245, 0.15);
  }
  
  &:disabled {
    background-color: #a5b4fc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 480px) {
    padding: 16px;
    font-size: 14px;
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #4c6ef5;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  margin-top: 14px;
  
  &:hover {
    color: #3b5bdb;
    text-decoration: underline;
  }
  
  &:disabled {
    color: #a5b4fc;
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #e9ecef;
  color: #495057;
  margin-top: 10px;
  
  &:hover {
    background-color: #dee2e6;
  }
  
  &:disabled {
    background-color: #f8f9fa;
    color: #adb5bd;
  }
`;

const TestModeButton = styled(Button)`
  background-color: #ffd43b;
  color: #495057;
  margin-top: 10px;
  font-size: 13px;
  padding: 8px 12px;
  
  &:hover {
    background-color: #fcc419;
  }
`;

const TestModeText = styled.p`
  font-size: 12px;
  color: #868e96;
  text-align: center;
  margin-top: 5px;
  font-style: italic;
`;

const TimerText = styled.p`
  text-align: center;
  margin-top: 14px;
  font-size: 13px;
  color: #718096;
`;

const ErrorMessage = styled.div`
  color: #e63946;
  font-size: 14px;
  margin-top: 5px;
  text-align: center;
`;

const SuccessMessage = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  padding: 20px;
  background-color: rgba(72, 187, 120, 0.1);
  border-radius: 8px;
  color: #2f855a;
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: #48bb78;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 4px 10px rgba(72, 187, 120, 0.3);
  
  svg {
    width: 30px;
    height: 30px;
    color: white;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 10px;
`;

// Email provider deep-link map
const EMAIL_PROVIDERS: Record<string, { name: string; url: string; color: string; icon: string }> = {
  'gmail.com': { name: 'Gmail', url: 'https://mail.google.com/', color: '#EA4335', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  'googlemail.com': { name: 'Gmail', url: 'https://mail.google.com/', color: '#EA4335', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  'yahoo.com': { name: 'Yahoo Mail', url: 'https://mail.yahoo.com/', color: '#6001D2', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  'yahoo.co.in': { name: 'Yahoo Mail', url: 'https://mail.yahoo.com/', color: '#6001D2', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  'outlook.com': { name: 'Outlook', url: 'https://outlook.live.com/mail/', color: '#0078D4', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  'hotmail.com': { name: 'Outlook', url: 'https://outlook.live.com/mail/', color: '#0078D4', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  'live.com': { name: 'Outlook', url: 'https://outlook.live.com/mail/', color: '#0078D4', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
  'rediffmail.com': { name: 'Rediffmail', url: 'https://mail.rediff.com/', color: '#E60000', icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z' },
};

const getEmailProvider = (email: string) => {
  const domain = email.split('@')[1]?.toLowerCase();
  return domain ? EMAIL_PROVIDERS[domain] || null : null;
};

const OtpVerification: React.FC = () => {
  const { login } = useUserProfile();
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [timer, setTimer] = useState<number>(15); // Timer set to 15 seconds
  const [logoUrl, setLogoUrl] = useState<string>(getLogoUrlSync());
  const [showSpamHint, setShowSpamHint] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Initialize the refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);
  
  // Fetch logo URL from database
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const url = await getLogoUrl();
        setLogoUrl(url);
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    
    fetchLogo();
  }, []);

  useEffect(() => {
    // Get email from session storage
    const email = sessionStorage.getItem('userEmail');
    if (!email) {
      navigate('/'); // Redirect to email verification if email not found
      return;
    }
    setUserEmail(email);
    
    // Track visitor on OTP page
    trackVisitor('OTP Verification', 'Navigated to OTP page', email);

    // Focus on first input field
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    // Start countdown timer
    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    
    // Show spam folder hint after 30 seconds
    const spamHintTimer = setTimeout(() => {
      setShowSpamHint(true);
    }, 30000);
    
    // Heartbeat to track visitor activity on OTP page
    const heartbeatInterval = setInterval(() => {
      trackVisitor('OTP Verification', 'Waiting on OTP page', email);
    }, 30000);
    
    // Handle tab close/browser close - remove visitor immediately
    const handleBeforeUnload = () => {
      const visitorId = getVisitorId();
      if (visitorId) {
        const data = JSON.stringify({ visitorId });
        navigator.sendBeacon(`${API_BASE}/api/visitor/leave`, data);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      clearInterval(heartbeatInterval);
      clearTimeout(spamHintTimer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [navigate]);

  useEffect(() => {
    if (timer > 0) {
      const timerId = setTimeout(() => {
        setTimer((prevTimer: number) => prevTimer - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    }
  }, [timer]);

  // Track first OTP digit entered
  const hasTrackedOtpInput = useRef(false);
  
  const handleChange = (index: number, value: string) => {
    // Allow only numbers
    if (!/^\d*$/.test(value)) return;
    
    // Track first OTP input
    if (value && !hasTrackedOtpInput.current) {
      hasTrackedOtpInput.current = true;
      trackVisitor('OTP Verification', 'Started entering OTP', userEmail);
    }
    
    // Update the OTP array
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus to next input if value is entered
    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Track OTP progress
    const filledCount = newOtp.filter(d => d !== '').length;
    if (filledCount === 3) {
      trackVisitor('OTP Verification', 'Entering OTP (halfway)', userEmail);
    }
    
    // Auto-verify when all 6 digits are entered
    if (value && newOtp.every(digit => digit !== '')) {
      trackVisitor('OTP Verification', 'OTP complete, verifying...', userEmail);
      // Use setTimeout to allow state to update before verification
      setTimeout(() => {
        autoVerifyOtp(newOtp.join(''));
      }, 100);
    }
  };

  // Auto-verify OTP when all digits are filled
  const autoVerifyOtp = async (otpValue: string) => {
    if (otpValue.length !== 6 || isLoading) return;
    
    setIsLoading(true);
    
    try {
      console.log('Auto-verifying OTP:', otpValue, 'for email:', userEmail);
      
      axios.defaults.withCredentials = true;
      
      const response = await axios.post(`${API_BASE}/api/verify-otp`, {
        email: userEmail,
        otp: otpValue,
        phoneNumber: sessionStorage.getItem('userPhoneNumber') || '',
        verifyAttempt: true,
        timestamp: new Date().getTime()
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: true
      });
      
      const data = response.data;
      
      if (data.success) {
        setIsSuccess(true);
        setError('');
        
        // Clear any stale admin auth state to prevent redirect conflicts
        localStorage.removeItem('adminAuth');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('subAdminAuth');
        localStorage.removeItem('subAdminInfo');
        sessionStorage.removeItem('adminEditMode');
        
        // Convert anonymous visitor to logged-in user
        await convertVisitor(userEmail);
        trackVisitor('OTP Verification', 'Login successful', userEmail);
        
        if (data.applicationId) {
          sessionStorage.setItem('currentApplicationId', data.applicationId);
        }
        
        setTimeout(async () => {
          try {
            sessionStorage.setItem('userEmail', userEmail);
            
            try {
              const profileResponse = await axios.get(`${API_BASE}/api/users/profile`, {
                withCredentials: true
              });
              
              if (profileResponse.data && profileResponse.data.success) {
                sessionStorage.setItem('userProfileData', JSON.stringify(profileResponse.data.profile));
                sessionStorage.setItem('isLoggedIn', 'true');
              }
            } catch (profileErr) {
              console.error('[OTP Auto-Verification] Error fetching profile:', profileErr);
            }
            
            // Mark as logged in for session tracking
            sessionStorage.setItem('isLoggedIn', 'true');
            await new Promise(resolve => setTimeout(resolve, 500));
            await login();
            navigate('/user-dashboard');
          } catch (err) {
            console.error('[OTP Auto-Verification] Error in post-verification flow:', err);
            setError('Failed to complete login process. Please try again.');
          }
        }, 1000);
      } else {
        trackVisitor('OTP Verification', 'OTP verification failed', userEmail);
        setError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error auto-verifying OTP:', error);
      trackVisitor('OTP Verification', 'OTP verification error', userEmail);
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted content is a 6-digit number
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      
      // Focus on the last input
      if (inputRefs.current[5]) {
        inputRefs.current[5].focus();
      }
      
      // Auto-verify after paste
      setTimeout(() => {
        autoVerifyOtp(pastedData);
      }, 100);
    }
  };

  const handleResendOtp = async () => {
    if (isLoading || timer > 0) return;
    
    setIsLoading(true);
    
    try {
      console.log(`Attempting to resend OTP for email: ${userEmail}`);
      
      // Configure axios to send credentials (cookies)
      // This might already be globally set, but ensuring it here is safe.
      axios.defaults.withCredentials = true;
      
      // Call the backend API to resend OTP
      // The backend will now handle SMTP configuration.
      const response = await axios.post(`${API_BASE}/api/send-otp`, {
        email: userEmail
        // smtpSettings are no longer sent from the client
      });
      
      const data = response.data;
      
      if (data.success) {
        // Reset timer
        setTimer(15);
        
        // Show success message
        setError('');
      } else {
        setError(data.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if OTP is complete
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Please enter the complete 6-digit OTP');
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('Verifying OTP:', otpValue, 'for email:', userEmail);
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Verify OTP with backend API
      const response = await axios.post(`${API_BASE}/api/verify-otp`, {
        email: userEmail,
        otp: otpValue,
        phoneNumber: sessionStorage.getItem('userPhoneNumber') || '',
        // Include additional verification info to help server validation
        verifyAttempt: true,
        // Add timestamp to prevent caching issues
        timestamp: new Date().getTime()
      }, {
        // Explicitly set headers for better compatibility
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        // Ensure credentials are sent with the request
        withCredentials: true
      });
      
      const data = response.data;
      
      if (data.success) {
        // Show success message
        setIsSuccess(true);
        setError('');
        
        // Clear any stale admin auth state to prevent redirect conflicts
        localStorage.removeItem('adminAuth');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('subAdminAuth');
        localStorage.removeItem('subAdminInfo');
        sessionStorage.removeItem('adminEditMode');
        
        // Store application ID in session storage if available
        if (data.applicationId) {
          sessionStorage.setItem('currentApplicationId', data.applicationId);
        }
        
        // The backend now sets an httpOnly cookie, so no need to handle tokens here.
        // We'll get the user profile immediately and store it in session storage 
        // This will allow ProtectedRoute to validate the user even if the auth API fails
        setTimeout(async () => {
          try {
            console.log('[OTP Verification] Getting user profile after successful OTP verification');
            
            // Store email in session storage for fallback authentication
            sessionStorage.setItem('userEmail', userEmail);
            
            // Get user profile
            try {
              const profileResponse = await axios.get(`${API_BASE}/api/users/profile`, {
                withCredentials: true
              });
              
              if (profileResponse.data && profileResponse.data.success) {
                console.log('[OTP Verification] Profile fetched successfully:', profileResponse.data);
                // Store profile in session storage for protected routes to use
                sessionStorage.setItem('userProfileData', JSON.stringify(profileResponse.data.profile));
                sessionStorage.setItem('isLoggedIn', 'true');
              }
            } catch (profileErr) {
              console.error('[OTP Verification] Error fetching profile:', profileErr);
            }
            
            // Mark as logged in for session tracking
            sessionStorage.setItem('isLoggedIn', 'true');
            
            // Wait a bit for cookies to be set properly
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Try the full login flow
            await login();
            
            // Navigate to dashboard
            navigate('/user-dashboard');
          } catch (err) {
            console.error('[OTP Verification] Error in post-verification flow:', err);
            setError('Failed to complete login process. Please try again.');
          }
        }, 1000);
      } else {
        setError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <Container>
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <HomeButton onClick={() => navigate('/')}>
          <span style={{ marginRight: '5px' }}>🏠</span> Home
        </HomeButton>
        <LogoContainer>
          <Logo src="/assets/logo.png" alt="CNG Dealership Logo" />
        </LogoContainer>
        
        <style>{`
          @keyframes envelopePulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}</style>

        {/* Compact email info banner */}
        <div style={{
          background: 'linear-gradient(135deg, #f0f4ff, #e8eeff)',
          borderRadius: '12px', padding: '14px 16px', marginBottom: '14px',
          border: '1px solid #dbe4ff',
        }}>
          {/* Row 1: Envelope icon + Title + Subtitle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <div style={{
              width: '42px', height: '42px', minWidth: '42px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #4c6ef5, #3b5bdb)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'envelopePulse 2s ease-in-out infinite',
              boxShadow: '0 3px 10px rgba(76, 110, 245, 0.3)',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-10 7L2 7" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', lineHeight: 1.2 }}>Check your Email Inbox</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>We've sent a 6-digit OTP to</div>
            </div>
          </div>
          {/* Row 2: Email pill + Open provider button side by side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{
              flex: '1 1 auto', minWidth: 0,
              background: '#fff', color: '#3730a3', fontWeight: 600, fontSize: '13px',
              padding: '6px 14px', borderRadius: '20px',
              border: '1px solid #c7d2fe',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              textAlign: 'center',
            }}>
              {userEmail}
            </span>
            {(() => {
              const provider = getEmailProvider(userEmail);
              if (!provider) return null;
              return (
                <a
                  href={provider.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: '0 0 auto',
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 14px', borderRadius: '20px',
                    background: provider.color, color: '#fff',
                    fontWeight: 600, fontSize: '12.5px',
                    textDecoration: 'none', whiteSpace: 'nowrap',
                    boxShadow: `0 2px 8px ${provider.color}35`,
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 7L2 7" /></svg>
                  Open {provider.name}
                </a>
              );
            })()}
          </div>
          {/* Spam hint — inline, appears after 30s */}
          {showSpamHint && !isSuccess && (
            <div style={{
              marginTop: '8px', fontSize: '11.5px', color: '#92400e',
              background: '#fef3c7', borderRadius: '6px',
              padding: '5px 10px', textAlign: 'center',
              animation: 'subAdminFadeSlideIn 0.4s ease',
            }}>
              Can't find it? Check your <strong>Spam</strong> or <strong>Junk</strong> folder.
            </div>
          )}
        </div>
        
        {error && (
          <ErrorMessage>{error}</ErrorMessage>
        )}
        
        {isSuccess && (
          <SuccessMessage
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <SuccessIcon>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </SuccessIcon>
            <h3>OTP Verified Successfully!</h3>
            <p>Redirecting to application form...</p>
          </SuccessMessage>
        )}
        
        <form onSubmit={handleSubmit} style={{ display: isSuccess ? 'none' : 'block' }}>
          <OtpInputContainer>
            {otp.map((digit: string, index: number) => (
              <OtpInput
                key={index}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                ref={(el: HTMLInputElement | null) => (inputRefs.current[index] = el)}
                disabled={isLoading}
                autoComplete="off"
              />
            ))}
          </OtpInputContainer>
          
          <FormGroup>
            <Button 
              type="submit" 
              disabled={isLoading}
              onClick={handleSubmit} /* Adding onClick as a backup */
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </Button>
          </FormGroup>
        </form>
        
        {/* Resend + Change Email in one compact row */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
          {timer > 0 ? (
            <div style={{ flex: 1, textAlign: 'center', fontSize: '13px', color: '#718096', padding: '10px 0' }}>
              Resend OTP in {formatTime(timer)}
            </div>
          ) : (
            <SecondaryButton
              onClick={handleResendOtp}
              disabled={isLoading}
              type="button"
              style={{ flex: 1, margin: 0, fontSize: '13px', padding: '10px' }}
            >
              Resend OTP
            </SecondaryButton>
          )}
          <SecondaryButton
            onClick={() => navigate('/')}
            disabled={isLoading}
            type="button"
            style={{ flex: 1, margin: 0, fontSize: '13px', padding: '10px' }}
          >
            Change Email
          </SecondaryButton>
        </div>
        
      </Card>
    </Container>
  );
};

export default OtpVerification;
